import { useId, useState, type ReactNode } from 'react'

import { Switch } from '@components/atoms/Switch'
import Icon from '@components/utils/Icon'
import { SegmentedControl } from '@components/atoms/SegmentedControl'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('embeds.editor')

type Device = 'desktop' | 'tablet' | 'mobile'

const DEVICE_WIDTH: Record<Device, string> = { desktop: '100%', tablet: '768px', mobile: '375px' }

type SampleSwitch = {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  label: string
}

type PreviewShellProps = {
  host: string
  control?: ReactNode
  sampleSwitch?: SampleSwitch
  note?: string
  children: ReactNode
}

export function PreviewShell({ host, control, sampleSwitch, note, children }: PreviewShellProps) {
  const [device, setDevice] = useState<Device>('desktop')
  const sampleSwitchId = useId()

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:px-4 tw:py-2.5">
        <span className="tw:text-sm tw:font-bold tw:text-gray-cool-700">{t('preview')}</span>
        <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-4">
          {control}
          {sampleSwitch && (
            <div className="tw:flex tw:items-center tw:gap-2">
              <label
                htmlFor={sampleSwitchId}
                className="tw:m-0 tw:cursor-pointer tw:text-xs tw:font-medium tw:text-gray-cool-700"
              >
                {sampleSwitch.label}
              </label>
              <Switch
                id={sampleSwitchId}
                checked={sampleSwitch.checked}
                onCheckedChange={sampleSwitch.onCheckedChange}
              />
            </div>
          )}
          <SegmentedControl<Device>
            ariaLabel={t('preview_width')}
            value={device}
            onChange={setDevice}
            options={(['desktop', 'tablet', 'mobile'] as const).map((value) => ({
              value,
              label: t(`devices.${value}`),
            }))}
          />
        </div>
      </div>

      <div className="tw:rounded-b-xl tw:bg-gray-cool-100 tw:p-4 tw:xl:rounded-bl-none">
        {note && (
          <p className="tw:m-0 tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-white tw:px-3 tw:py-2 tw:text-xs tw:text-gray-cool-700 tw:shadow-xs">
            <Icon type="eye" className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-cool-500" />
            {note}
          </p>
        )}
        <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50">
          <div className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-gray-cool-200 tw:bg-white tw:px-3 tw:py-2">
            <span className="tw:flex tw:gap-1" aria-hidden>
              <span className="tw:h-2.5 tw:w-2.5 tw:rounded-full tw:bg-gray-300" />
              <span className="tw:h-2.5 tw:w-2.5 tw:rounded-full tw:bg-gray-300" />
              <span className="tw:h-2.5 tw:w-2.5 tw:rounded-full tw:bg-gray-300" />
            </span>
            <span className="tw:mx-auto tw:truncate tw:rounded-md tw:bg-gray-cool-50 tw:px-3 tw:py-0.5 tw:text-xs tw:text-gray-cool-600">
              {host}
            </span>
          </div>
          <div className="tw:overflow-x-auto tw:p-4">
            <section
              aria-label={t('preview')}
              className="tw:mx-auto tw:max-w-full tw:overflow-hidden tw:rounded-md tw:outline-dashed tw:outline-2 tw:outline-offset-8 tw:outline-primary-300 tw:transition-all tw:motion-reduce:transition-none"
              style={{ width: DEVICE_WIDTH[device] }}
            >
              {children}
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
